<template>
  <div class="editor-container">
    <div v-if="selectedElement" class="card">
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" mode="Editor" />
      <div class="card-body">
        <!-- Main Prompt -->
        <div class="mb-3">
          <h3>
            <input type="text" class="inputMainPrompt" v-model="selectedElement.el_Text"
              @input="updateElement('el_Text', selectedElement.el_Text)" placeholder="Main prompt" />
          </h3>
        </div>

        <!-- Global Quill Editor (Instruction) -->
        <div class="form-group mb-3">
          <div ref="quillEditor" class="quill-editor"></div>
        </div>

        <!-- Parameters -->
        <div class="row mb-4">
          <div class="col">
            <h5>Choices Parameters</h5>
          </div>
          <div class="col">
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-sm" :class="{ 'btn-primary': !selectedElement.multiple }"
                @click="setChoiceType(false)">Single choice</button>
              <button type="button" class="btn btn-sm" :class="{ 'btn-primary': selectedElement.multiple }"
                @click="setChoiceType(true)">Multiple choice</button>
            </div>
          </div>
          <div class="col">
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-sm" :class="{ 'btn-primary': !selectedElement.randomized }"
                @click="setRandom(false)">Fixed</button>
              <button type="button" class="btn btn-sm" :class="{ 'btn-primary': selectedElement.randomized }"
                @click="setRandom(true)">Shuffle</button>
            </div>
          </div>
        </div>

        <!-- Option blocks with QuillBlock component -->
        <div v-for="(option, index) in selectedElement.options" :key="option.id" class="mb-4">
          <div class="row" style="margin-bottom: 60px;">
            <div class="col-8">
              <QuillBlock 
                v-model="option.richText"  
                @update:modelValue="(val) => updateOption(index, 'richText', val)"/>
            </div>
            <div class="col-4">
              <div class="controls mt-2">
                <div class="optionScore" >
                  <div class="score-row">
                    <input type="checkbox" v-model="option.isCorrect" class="form-check-input"
                      @change="updateOption(index, 'isCorrect', option.isCorrect)"
                      title="If correct is checked, only positive weight, if not checked, only 0 or negative weight" />
                    <label class="form-check-label">Correct</label>
                  </div>

                  <div class="score-row weight-row">
                    <label class="weight-label">Weight:</label>
                    <input min="1" type="number" v-model="option.weight" @input="updateOption(index, 'weight', option.weight)"
                    class="form-control form-control-sm weight-input"
                    title="If correct is checked, only positive weight, if not checked, only 0 or negative weight" />
                  </div>

                  <div class="score-row delete-row">
                    <button @click="deleteOption(index)" class="btn btn-sm btn-danger w-100">
                      <i class="bi bi-trash"></i> Delete
                    </button>
                  </div>
                </div>
                
              </div>
            </div>
          </div>


        </div>

        <button class="btn btn-primary btn-sm mt-4" @click="addOption">+ Add Option</button>

        <!-- Block Style Customization -->
        <div class="style-customization mt-5">
          <h5 class="mb-3">Block Style Customization</h5>

          <div class="row g-3">
            <!-- Background Color -->
            <div class="col-md-4">
              <label class="form-label">Background Color</label>
              <input type="color" class="form-control form-control-color"
                v-model="blockStyle.backgroundColor"
                @input="updateBlockStyle('backgroundColor', blockStyle.backgroundColor)" />
            </div>

            <!-- Gradient Color -->
            <div class="col-md-4">
              <label class="form-label">Gradient Color (optional)</label>
              <input type="color" class="form-control form-control-color"
                v-model="blockStyle.gradientColor"
                @input="updateBlockStyle('gradientColor', blockStyle.gradientColor)" />
            </div>

            <!-- Gradient Direction -->
            <div class="col-md-4">
              <label class="form-label">Gradient Direction</label>
              <select class="form-select" v-model="blockStyle.gradientDirection"
                @change="updateBlockStyle('gradientDirection', blockStyle.gradientDirection)">
                <option value="to right">Horizontal →</option>
                <option value="to bottom">Vertical ↓</option>
                <option value="to bottom right">Diagonal ↘</option>
                <option value="135deg">Diagonal ↗</option>
              </select>
            </div>

            <!-- Border Color -->
            <div class="col-md-6">
              <label class="form-label">Border Color</label>
              <input type="color" class="form-control form-control-color"
                v-model="blockStyle.borderColor"
                @input="updateBlockStyle('borderColor', blockStyle.borderColor)" />
            </div>

            <!-- Border Width -->
            <div class="col-md-6">
              <label class="form-label">Border Width (px)</label>
              <input type="number" min="0" max="10" class="form-control"
                v-model.number="blockStyle.borderWidth"
                @input="updateBlockStyle('borderWidth', blockStyle.borderWidth)" />
            </div>

            <!-- Border Radius -->
            <div class="col-md-6">
              <label class="form-label">Border Radius (px)</label>
              <input type="number" min="0" max="50" class="form-control"
                v-model.number="blockStyle.borderRadius"
                @input="updateBlockStyle('borderRadius', blockStyle.borderRadius)" />
            </div>

            <!-- Width -->
            <div class="col-md-6">
              <label class="form-label">Width (%)</label>
              <input type="number" min="10" max="100" class="form-control"
                v-model.number="blockStyle.width"
                @input="updateBlockStyle('width', blockStyle.width)" />
            </div>

            <!-- Min Height -->
            <div class="col-md-6">
              <label class="form-label">Min Height (px)</label>
              <input type="number" min="50" max="500" class="form-control"
                v-model.number="blockStyle.minHeight"
                @input="updateBlockStyle('minHeight', blockStyle.minHeight)" />
            </div>

            <!-- Text Alignment -->
            <div class="col-md-6">
              <label class="form-label">Text Alignment</label>
              <select class="form-select" v-model="blockStyle.textAlign"
                @change="updateBlockStyle('textAlign', blockStyle.textAlign)">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
                <option value="justify">Justify</option>
              </select>
            </div>

            <!-- Font Size -->
            <div class="col-md-6">
              <label class="form-label">Font Size (px)</label>
              <input type="number" min="10" max="72" class="form-control"
                v-model.number="blockStyle.fontSize"
                @input="updateBlockStyle('fontSize', blockStyle.fontSize)" />
            </div>

            <!-- Font Color -->
            <div class="col-md-6">
              <label class="form-label">Font Color</label>
              <input type="color" class="form-control form-control-color"
                v-model="blockStyle.fontColor"
                @input="updateBlockStyle('fontColor', blockStyle.fontColor)" />
            </div>

            <!-- Padding -->
            <div class="col-md-6">
              <label class="form-label">Padding (px)</label>
              <input type="number" min="0" max="50" class="form-control"
                v-model.number="blockStyle.padding"
                @input="updateBlockStyle('padding', blockStyle.padding)" />
            </div>

            <!-- Margin -->
            <div class="col-md-6">
              <label class="form-label">Margin (px)</label>
              <input type="number" min="0" max="50" class="form-control"
                v-model.number="blockStyle.margin"
                @input="updateBlockStyle('margin', blockStyle.margin)" />
            </div>

            <!-- Option Index Type -->
            <div class="col-md-6">
              <label class="form-label">Option Index</label>
              <select class="form-select" v-model="blockStyle.indexType"
                @change="updateBlockStyle('indexType', blockStyle.indexType)">
                <option value="none">No Index</option>
                <option value="letters">Letters (A, B, C...)</option>
                <option value="numbers">Numbers (1, 2, 3...)</option>
              </select>
            </div>

            <!-- Background Image -->
            <div class="col-12">
              <label class="form-label">Background Image (Base64 or URL)</label>
              <div class="input-group">
                <input type="text" class="form-control"
                  v-model="blockStyle.backgroundImage"
                  @input="updateBlockStyle('backgroundImage', blockStyle.backgroundImage)"
                  placeholder="Paste image URL or Base64" />
                <label class="btn btn-outline-secondary" for="bgImageUpload">
                  Upload
                  <input type="file" id="bgImageUpload" accept="image/*"
                    @change="handleImageUpload" style="display: none;" />
                </label>
              </div>
            </div>

            <!-- Background Size -->
            <div class="col-md-6">
              <label class="form-label">Background Size</label>
              <select class="form-select" v-model="blockStyle.backgroundSize"
                @change="updateBlockStyle('backgroundSize', blockStyle.backgroundSize)">
                <option value="auto">Auto</option>
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
              </select>
            </div>

            <!-- Background Repeat -->
            <div class="col-md-6">
              <label class="form-label">Background Repeat</label>
              <select class="form-select" v-model="blockStyle.backgroundRepeat"
                @change="updateBlockStyle('backgroundRepeat', blockStyle.backgroundRepeat)">
                <option value="no-repeat">No Repeat</option>
                <option value="repeat">Repeat</option>
                <option value="repeat-x">Repeat X</option>
                <option value="repeat-y">Repeat Y</option>
              </select>
            </div>

            <!-- Reset Button -->
            <div class="col-12">
              <button class="btn btn-sm btn-outline-danger" @click="resetBlockStyle">
                Reset to Default
              </button>
            </div>
          </div>
        </div>

        <!-- Hint and Feedback -->
        <div class="form-floating hintContainer mt-4">
          <input type="text" class="form-control" placeholder="Hint" v-model="selectedElement.tip"
            @input="updateElement('tip', selectedElement.tip)" />
          <label>Hint</label>
        </div>
        <div class="form-floating feedback mt-4">
          <input type="text" class="form-control" placeholder="Positive feedback"
            v-model="selectedElement.feedback.correct"
            @input="updateElement('feedback.correct', selectedElement.feedback.correct)" />
          <label>Positive feedback</label>
        </div>
        <div class="form-floating feedback mt-4">
          <input type="text" class="form-control" placeholder="Negative feedback"
            v-model="selectedElement.feedback.incorrect"
            @input="updateElement('feedback.incorrect', selectedElement.feedback.incorrect)" />
          <label>Negative feedback</label>
        </div>
      </div>
            
      <!-- Slider de difficulté -->
      <DifficultySlider 
        v-model="selectedElement.difficulty_rating"
        @update:model-value="updateElement('difficulty_rating', $event)"
      />
      
      <dimension v-if="hasDimensions" />
      <div class="card-footer">
        <EditorActionBar :selectedElement="selectedElement" :elementLabel="elementLabel" :isItemBank="isItemBank" />
      </div>
    </div>
    <div v-else class="alert alert-info">Select an element to edit</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import Quill from 'quill';
import cardHeader from '../default/cardHeader.vue';
import EditorActionBar from '../default/EditorActionBar.vue';
import QuillBlock from './QuillBlock.vue';
import { useTestStore } from '../../../stores/testStore';
import dimension from '../default/dimension.vue'; 

import DifficultySlider from '../../common/DifficultySlider.vue';
const props = defineProps({
  isItemBank: Boolean
});

const store = useTestStore();
// Computed pour vérifier si les dimensions doivent être affichées
const hasDimensions = computed(() => {
  const selectedEl = store.selectedElement;
  const testData = store.testData;
  
  // Afficher si l'élément a déjà des dimensions assignées
  const hasAssignedDimensions = selectedEl?.dimension && selectedEl.dimension.length > 0;
  
  // Afficher si des dimensions sont disponibles pour être ajoutées
  const hasAvailableDimensions = testData?.dimensions && testData.dimensions.length > 0;
  
  return hasAssignedDimensions || hasAvailableDimensions;
});
const selectedElement = computed(() => {
  const element = store.selectedElement;
  if (!element) return { options: [], feedback: { correct: '', incorrect: '' } };

  // S'assurer que feedback existe avec les bonnes propriétés
  if (!element.feedback) {
    element.feedback = { correct: '', incorrect: '' };
  } else {
    if (element.feedback.correct === undefined) element.feedback.correct = '';
    if (element.feedback.incorrect === undefined) element.feedback.incorrect = '';
  }

  return element;
});
const elementLabel = computed(() => store.selectedElementLabel);

const quillEditor = ref(null);
let quillInstance = null;

onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
          ]
        }
      }
    });
    
    // Initialiser le contenu de Quill
    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText;
    }
    
    // Détecter les modifications et sauvegarder automatiquement
    quillInstance.on('text-change', () => {
      if (selectedElement.value && !props.isItemBank) {
        selectedElement.value.el_RichText = quillInstance.root.innerHTML;
        store.updateElement(selectedElement.value);
      }
    });
  }

  // Nouvelle logique pour s'assurer que chaque option ait un weight par défaut
  if (selectedElement.value?.options) {
    selectedElement.value.options.forEach(option => {
      if (option.weight === undefined || option.weight === null || option.weight === '') {
        option.weight = 1;
      }
    });

    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
});

// Observer uniquement el_RichText pour les mises à jour en temps réel
watch(
  () => selectedElement.value?.el_RichText,
  (newValue) => {
    if (quillInstance && newValue !== quillInstance.root.innerHTML) {
      quillInstance.root.innerHTML = newValue || '';
    }
  }
);

// Mise à jour automatique du store lorsque el_Text est modifié
watch(() => selectedElement.value?.el_Text, (newText) => {
  if (newText !== undefined && !props.isItemBank) {
    store.updateElement(selectedElement.value);
  }
});



const updateElement = (key, value) => {
  if (props.isItemBank) return;

  const keys = key.split('.');
  let target = selectedElement.value;
  while (keys.length > 1) target = target[keys.shift()];
  target[keys[0]] = value;
  store.updateElement(selectedElement.value);
};

const addOption = () => {
  const id = `opt_${Date.now()}`;
  selectedElement.value.options.push({ id, richText: '', isCorrect: false, weight: 1 });

  if (!props.isItemBank) {
    store.updateElement(selectedElement.value);
  }
};

const deleteOption = (index) => {
  selectedElement.value.options.splice(index, 1);

  if (!props.isItemBank) {
    store.updateElement(selectedElement.value);
  }
};

const updateOption = (index, key, value) => {
  selectedElement.value.options[index][key] = value;

  if (!props.isItemBank) {
    store.updateElement(selectedElement.value);
  }
};

const setChoiceType = (isMultiple) => updateElement('multiple', isMultiple);
const setRandom = (isRandom) => updateElement('randomized', isRandom);

// Block Style Management
const defaultBlockStyle = {
  backgroundColor: '#ffffff',
  gradientColor: '',
  gradientDirection: 'to right',
  borderColor: '#cccccc',
  borderWidth: 1,
  borderRadius: 8,
  width: 100,
  minHeight: 100,
  textAlign: 'left',
  fontSize: 16,
  fontColor: '#000000',
  padding: 16,
  margin: 8,
  indexType: 'letters',
  backgroundImage: '',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat'
};

const blockStyle = ref({ ...defaultBlockStyle });

// Initialize blockStyle from selectedElement
watch(
  () => selectedElement.value,
  (newElement) => {
    if (newElement?.blockStyle) {
      blockStyle.value = { ...defaultBlockStyle, ...newElement.blockStyle };
    } else {
      blockStyle.value = { ...defaultBlockStyle };
    }
  },
  { immediate: true }
);

const updateBlockStyle = (key, value) => {
  if (props.isItemBank) return;

  blockStyle.value[key] = value;

  if (!selectedElement.value.blockStyle) {
    selectedElement.value.blockStyle = {};
  }

  selectedElement.value.blockStyle = { ...blockStyle.value };
  store.updateElement(selectedElement.value);
};

const resetBlockStyle = () => {
  blockStyle.value = { ...defaultBlockStyle };
  selectedElement.value.blockStyle = { ...defaultBlockStyle };

  if (!props.isItemBank) {
    store.updateElement(selectedElement.value);
  }
};

const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    const base64Image = e.target.result;
    blockStyle.value.backgroundImage = base64Image;
    updateBlockStyle('backgroundImage', base64Image);
  };
  reader.readAsDataURL(file);
};
</script>

<style scoped>
.quill-editor {
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 1rem;
}

.card-header {
  background-color: #426C9C;
  color: white;
}

.btn-group {
  border: 1px solid #ccc;
}
.optionScore{
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.score-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.score-row label {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.form-check-input {
  cursor: pointer;
  width: 20px;
  height: 20px;
  margin: 0;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.weight-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.weight-label {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.weight-input {
  width: 60px;
  text-align: center;
  font-weight: 600;
}

.delete-row {
  margin-top: 4px;
}

.btn-danger {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 12px;
  transition: all 0.2s ease;
}

.btn-danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.style-customization {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 10px;
  padding: 20px;
}

.style-customization h5 {
  color: #495057;
  font-weight: 600;
  border-bottom: 2px solid #426C9C;
  padding-bottom: 10px;
}

.form-control-color {
  height: 45px;
  cursor: pointer;
}

</style>
